import {WorksitesTableProps} from "./types";
import React, {createRef, useCallback, useContext, useEffect, useMemo, useState} from "react";
import UserNotificationsContext from "../../../../UserNotificationsContext";
import TableStyle from "@components/TableStyle";
import MaterialTable from "@material-table/core";
import {tableIcons} from "@ui-components/TableIcons";
import {SharingInfoModal} from "@components/Modals/SharingInfoModal";
import AuthenticationService from "@services/AuthenticationService";
import {useHomeWorksites} from "@feature/worksites/home/hooks/useHomeWorksites";
import {useIntl} from "react-intl";
import {WorksitesWithIdSchema} from "@feature/types";
import {getColumns, OPTIONS, LOCALIZATION} from "@feature/worksites/home/table/lib/utils";
import {ADVANCED_CONTROL_ROLES} from "@feature/worksites/home/filters/lib/consts";
import _ from "lodash";
import {removeSharing as removeSharingUtil} from "../../../../utils/sharing";
import useCountryAxiosInstance from "../../../../hooks/useCountryAxiosInstance";
import {NotificationsContext} from "@ui-components/Notifications";
import {useHistory} from "react-router-dom";
import CountryContext from "../../../../CountryContext";

export const WorksitesTable = ({filters, users, refreshObservable}: WorksitesTableProps) => {

  // context
  const { userNotifications } = useContext(UserNotificationsContext) as any;
  const { push } = useContext(NotificationsContext) as any;
  const { country } = useContext(CountryContext) as any;

  // hooks
  const intl = useIntl();
  const history = useHistory();
  const countryApiRequest = useCountryAxiosInstance();
  const {loading, fetchWorksitesPage: fetch} = useHomeWorksites();

  // state
  const [showSharing, setShowSharing] = useState<any>(false);
  const [selectedWorksiteForShowingSharing, setSelectedWorksiteForShowingSharing] = useState<WorksitesWithIdSchema | null>(null);

  // ref
  const ref = createRef<MaterialTable<WorksitesWithIdSchema>>();

  // utils
  const userData = AuthenticationService.getUserData();
  const showFlgTest = ADVANCED_CONTROL_ROLES.includes(userData.role);
  const columns = useMemo(
    () => getColumns(intl, showFlgTest, setShowSharing, users, userNotifications, setSelectedWorksiteForShowingSharing),
    [users, userNotifications],  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const debouncedQueryChange = useCallback(() => {
      // @ts-ignore
      ref.current?.onQueryChange();
    },
    [ref, refreshObservable]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Fetch sharing for the selected worksite (not needed here since we're in a list view)
  const fetchSharing = useCallback(() => {
    // In the list view, we refresh the table instead
    debouncedQueryChange();
  }, [debouncedQueryChange]);

  /**
   * Remove the share of the worksite only if the user is admin or the creator.
   * Wrapper function that calls the utility with all required parameters.
   */
  const removeSharing = async (user: any) =>  {
    await removeSharingUtil({
      user,
      selectedWorksite: selectedWorksiteForShowingSharing,
      countryApiRequest,
      push,
      intl,
      userData,
      history,
      country,
      showSharing,
      setShowSharing,
      fetchSharing,
    });
    setShowSharing(false);
  }

  useEffect(() => {
    debouncedQueryChange();
  }, [JSON.stringify(filters), refreshObservable, showSharing]);

  return (
    <>
      <TableStyle>
        <MaterialTable<WorksitesWithIdSchema>
          tableRef={ref}
          columns={columns}
          data={(query) => fetch(filters, query)}
          icons={tableIcons}
          isLoading={loading}
          options={OPTIONS}
          localization={LOCALIZATION}
        />
      </TableStyle>
      {showSharing &&
        <SharingInfoModal
          worksite={selectedWorksiteForShowingSharing}
          showSharing={showSharing}
          setShowSharing={setShowSharing}
          intl={intl}
          removeSharing={removeSharing}
        />
      }
    </>
  );
}